import {
  CostLineTypes,
  EstimateAcceptedRejectedRevisionBy,
  EstimateDetailTypeEnum,
  EstimateStatusTypes,
  TagModalTypeEnum,
  TagTypes,
} from '../enums/estimate/estimate.enum';
import { Address } from '../interfaces/address.interface';
import { Customer, Project } from '../interfaces/customer.interface';
import { EstimateVisibility, IEstimateTotal } from '../interfaces/estimate/estimate.interface';
import { Files } from '../interfaces/files.interface';
import { TaxEntity } from '../interfaces/tax/tax.interface';
import { InvoiceEntityDTO } from '../invoice/invoice.interface';
import type { EstimateDocumentHeaderOverride, EstimateHeaderOverrideCarrier } from '../utils/estimate-document-header';
import type { ClientVisibleVariable } from '../utils/estimate-variables';

export class EstimateEntityDTO {
  Id: string;
  Name: string;
  IsTemplate: boolean;
  /** Left uninitialised on purpose: the header override blob bypasses this constructor entirely. */
  LogoSize?: number;
  LicenseNumberName: string;
  LicenseNumber: string;
  CompanyName: string;
  CompanyPhoneNo: string | any;
  CompanyEmail: string;
  CompanyWebsite: string;
  CompanyAddress: Address;
  CustomerFirstName: string;
  CustomerLastName: string;
  CustomerPhone: string;
  CustomerEmail: string;
  CustomerAddress: Address;
  CustomerBillingAddress: Address;
  ShowBillingAddressToCustomer: boolean;
  ProjectName: string;
  ProjectNumber: string;
  ProjectAddress: Address;
  ShowProjectInfoToCustomer: boolean;
  Visibility: EstimateVisibility;
  IsItemized: boolean;
  ClosedDate?: string;
  CustomerId: string;
  ProjectId: string;
  FileId: string;
  CreatedDate: Date;
  Files: Files[] = [];
  ItemsFiles: Files[] = [];
  EstimateDetails: EstimateDetailsEntityDTO[]; //Current details
  EstimateDetailsList: EstimateDetailsEntityDTO[]; //All details
  EstimateValues: EstimateValuesDTO;
  Customer: Customer;
  ProjectV2Entities: Project;
  EstimateAlias: string;
  EstimateInvoicedPercentage: number;
  Discounts: LinkedDiscountDTO[];
  DefaultPrintedPhotoSize: number;
  IsPublished: boolean;
  RequireTaxPerItemizedCostLineIsDisabled: boolean;
  Invoices: InvoiceEntityDTO[];
  EstimateInvoicedProgress: number;
  EstimateInvoicePaidProgress: number;
  constructor(private data?: EstimateEntityDTO) {
    if (data) {
      this.Id = data.Id;
      this.Name = data.Name;
      this.IsTemplate = data.IsTemplate;
      this.LicenseNumberName = data.LicenseNumberName;
      this.LicenseNumber = data.LicenseNumber;
      this.CompanyName = data.CompanyName;
      this.CompanyPhoneNo = data.CompanyPhoneNo;
      this.CompanyEmail = data.CompanyEmail;
      this.CompanyWebsite = data.CompanyWebsite;
      this.CompanyAddress = data.CompanyAddress;
      this.CustomerFirstName = data.CustomerFirstName;
      this.CustomerLastName = data.CustomerLastName;
      this.CustomerEmail = data.CustomerEmail;
      this.CustomerPhone = data.CustomerPhone;
      this.CustomerAddress = data.CustomerAddress;
      this.CustomerBillingAddress = data.CustomerBillingAddress;
      this.ShowBillingAddressToCustomer = data.ShowBillingAddressToCustomer;
      this.ProjectName = data.ProjectName;
      this.ProjectNumber = data.ProjectNumber;
      this.ProjectAddress = data.ProjectAddress;
      this.ShowProjectInfoToCustomer = data.ShowProjectInfoToCustomer;
      this.Visibility = data.Visibility;
      this.IsItemized = data.IsItemized;
      this.CustomerId = data.CustomerId;
      this.ProjectId = data.ProjectId;
      this.FileId = data.FileId;
      this.CreatedDate = data.CreatedDate;
      this.EstimateDetails = data.EstimateDetails;
      this.Files = data.Files;
      this.Customer = data.Customer;
      this.EstimateInvoicedPercentage = data.EstimateInvoicedPercentage;
      this.Discounts = data.Discounts;
      this.DefaultPrintedPhotoSize = data.DefaultPrintedPhotoSize;
      this.IsPublished = data.IsPublished;
      this.RequireTaxPerItemizedCostLineIsDisabled = data.RequireTaxPerItemizedCostLineIsDisabled;
    }
  }
}

export class EstimateDetailsEntityDTO implements EstimateHeaderOverrideCarrier {
  Id: string = '';
  EstimateType: EstimateDetailTypeEnum = EstimateDetailTypeEnum.Normal;
  Name: string = 'Estimate';
  Title: string = '';
  EstimateNumber: string = '';
  EstimateDate: Date = new Date();
  EstimateExpiresDate: Date = new Date(new Date().setMonth(new Date().getMonth() + 1));
  EstimateIntroduction: string = '';
  EstimateContactDetails: string = '';
  CustomerCanView: boolean = false;
  CustomerCanApproveReject: boolean = false;
  CustomerCanApproveAfterExpirationDate: boolean = false;
  EstimateDetailsSections: EstimateDetailsSectionEntityDTO[] = [];
  ShowDisplayName: boolean = false;
  ShowEstimateNumber: boolean = false;
  LockEstimate: boolean = false;
  FilesList: any[] = [];
  PhotoList: any[] = [];
  EstimateTotal: IEstimateTotal;
  IsBreakdown: boolean = false;
  IsMenuCollapse: boolean = false;
  Status: EstimateStatusTypes = EstimateStatusTypes.Draft;
  StatusProps?: EstimateStatusPropsDTO;
  CustomFields?: string;
  CustomFieldsData?: { Name: any; Value: any }[];
  /** This document's own printed header once it stops inheriting the estimate's: stored blob, parsed companion. */
  HeaderOverride?: string | null;
  HeaderOverrideData?: EstimateDocumentHeaderOverride | null;
  /** Estimate-scope named variables: the stored JSON, and the rows the mapper lets the customer see. */
  Variables?: string | null;
  ClientVisibleVariables: ClientVisibleVariable[] = [];
  AcceptedRejectedRevisionBy: EstimateAcceptedRejectedRevisionBy;
  CustomerFullName: string;
  Reason: string;
  ApprovedDate: Date;
  RejectionDate: null;
  RequestRevisionDate: null;
  EstimateId: string;
  CreatedDate: Date;
  UpdatedDate: Date;
  DeletedDate: null;
  CreatedBy: string;
  IsDeleted: boolean;
  PreviousTotal?: number;
  RevisedTotal?: number;
  EstimateDiscounts: LinkedDiscountDTO[];
  TotalDiscount: number;
  TaxId: string;
  TaxEntity: TaxEntity;
  SubTotal: number;
  TaxableTotal: number;
  TotalTax: number;
  TotalWithTax: number;
  TotalWithoutTax: number;
  Tax: any;
  /** Set by the editor's "Show Unselected" toggle. The client has no control over this — it only reads the DB value. */
  ShowUnselectedInPrint: boolean;

  constructor(private data?: EstimateDetailsEntityDTO) {
    if (data) {
      this.Id = data.Id;
      this.EstimateType = data.EstimateType;
      this.Name = data.Name;
      this.EstimateNumber = data.EstimateNumber;
      this.EstimateDate = new Date(data.EstimateDate);
      this.EstimateExpiresDate = new Date(data.EstimateExpiresDate);
      this.EstimateIntroduction = data.EstimateIntroduction;
      this.EstimateContactDetails = data.EstimateContactDetails;
      this.CustomerCanView = data.CustomerCanView;
      this.CustomerCanApproveReject = data.CustomerCanApproveReject;
      this.CustomerCanApproveAfterExpirationDate = data.CustomerCanApproveAfterExpirationDate;
      this.ShowDisplayName = data.ShowDisplayName;
      this.ShowEstimateNumber = data.ShowEstimateNumber;
      this.Status = data.Status;
      this.LockEstimate = data.LockEstimate;
      this.FilesList = data.FilesList;
      this.PhotoList = data.PhotoList;
      this.EstimateDetailsSections = data.EstimateDetailsSections;
      this.StatusProps = data.StatusProps;
      this.CustomFields = data.CustomFields;
      this.CustomFieldsData = data.CustomFieldsData;
      this.HeaderOverride = data.HeaderOverride;
      this.HeaderOverrideData = data.HeaderOverrideData;
      this.Variables = data.Variables;
      this.ClientVisibleVariables = data.ClientVisibleVariables;
      this.AcceptedRejectedRevisionBy = data.AcceptedRejectedRevisionBy;
      this.CustomerFullName = data.CustomerFullName;
      this.ApprovedDate = data.ApprovedDate;
      this.EstimateDiscounts = data.EstimateDiscounts;
      this.Title = data.Title;
      this.TaxId = data.TaxId;
      this.TaxEntity = data.TaxEntity;
      this.UpdatedDate = data.UpdatedDate;
      this.CreatedDate = data.CreatedDate;
      this.IsDeleted = data.IsDeleted;
      this.Reason = data.Reason;
      this.RejectionDate = data.RejectionDate;
      this.RequestRevisionDate = data.RequestRevisionDate;
      this.PreviousTotal = data.PreviousTotal;
      this.RevisedTotal = data.RevisedTotal;
      this.EstimateId = data.EstimateId;
      this.EstimateTotal = data.EstimateTotal;
      this.SubTotal = data.SubTotal;
      this.TaxableTotal = data.TaxableTotal;
      this.TotalTax = data.TotalTax;
      this.TotalWithTax = data.TotalWithTax;
      this.TotalWithoutTax = data.TotalWithoutTax;
      this.Tax = data.Tax;
      this.ShowUnselectedInPrint = data.ShowUnselectedInPrint;
    }
  }
}



export class EstimateDetailsSectionEntityDTO {
  Id: string = '';
  Name: string = '';
  IsTemplate: boolean = false;
  IsViewable: boolean = true;
  IsTaxable: boolean;
  CalculatedTax: number;
  ParentSectionId: string = '';
  ShowEstimateSectionName: boolean = false;
  IsOpen: boolean = true;
  IsAssemblyOn: boolean = false;
  IsDisplayOnly: boolean = false;
  RemovedBySectionId?: string;
  ModifiedFromSectionDetailsNumber?: string;
  ModifiedFromSectionDetailsType?: EstimateDetailTypeEnum;
  ModifiedFromSectionId?: string;
  PriceIsVisible: boolean = false;
  QtyIsVisible: boolean = false;
  /** Section-scope named variables: the stored JSON, and the rows the mapper lets the customer see. */
  Variables?: string | null;
  ClientVisibleVariables: ClientVisibleVariable[] = [];
  UnitType: string;
  PerUnit: number;
  Units: number;
  /** The printed price column shows this section's "N UNIT x rate" line: every unit cell and colspan reads it. */
  ShowsSectionUnit?: boolean;
  Order: number;
  SectionWiseOrder: number;
  SubEstimateDetails: EstimateDetailsSectionEntityDTO[] = [];
  EstimateCostLineItems: EstimateCostLineItemsEntityDTO[] = [];
  SectionOrder: number;
  displayList: EstimateSectionsDisplayListDTO[];
  Total: number = 0;
  TaxableTotal: number;
  IsSelectionEnabled: boolean = false;
  SelectionType: number;
  IsSelectedForSelection: boolean;
  SelectionSomeSelect: boolean;
  SelectionAllSelect: boolean;
  NoneSelected: boolean;
  ItemsFiles: Files[];
}

export class EstimateFilesDTO {
  FileId: string;
  FileName: string;
  FileNotes: string;
}

export class EstimateCostLineItemsEntityDTO {
  Id: string;
  Name: string = '';
  CostLineType: CostLineTypes; //= CostLineTypes.Labor;
  Description: string = '';
  Price: number = 0; //Price for customer
  IsTaxable: boolean; // = true;
  IsTaxCompanyCostOnly: boolean; // = true;
  IsViewable: boolean = true; //default
  QtyIsVisible: boolean; // = true; //default
  QtyIsVisibleItemized: boolean;
  Order: number;
  IsTemplate: boolean;
  Vendor: string;
  Sku: string;
  DefaultQty: number;
  DefaultCostPer: number;
  MinimumMarkupType: string;
  MinimumMarkupPer: number;
  DefaultMarkupType: string;
  DefaultMarkupPer: number;
  // A share link states these in place of the company cost and markup it withholds.
  CustomerPrice?: number;
  CustomerUnitPrice?: number;
  TaxableCompanyCost?: number | null;
  SourceUrl: string;
  CategoryId: string;
  TradeId: string;
  UnitType: string;
  IsOpen: boolean = false;
  OnlyShowCategoryName: boolean; // = false;
  Props: any;
  ShowQtyTextBox: boolean = false;
  ShowCostPerTxtBox: boolean = false;
  SectionWiseOrder: number;
  isTemplateOpen: boolean = false;
  //calculatedValues: calculatedCostlineValuesDTO | any;
  IsDirty = false;
  FileId: string;
  TaxId: string;
  TaxEntity: TaxEntity;
  InvoicedPrice: number;
  //calculated Values
  calculatedMarkup = 0;
  calculatedCompanyCost = 0;
  calculatedCustomerCost = 0;
  calculatedMinimumMarkup = 0;
  CalculatedTax = 0;
  Category?: any;
  Units: number;
  taxableAmt = 0;
  IsSelectedForSelection: boolean = false;
  ItemsFiles: Files[];
  IsMargin: boolean = false;
  CreatedDate: Date;
  calculatedTax = 0;

  constructor(data?: EstimateCostLineItemsEntityDTO) {
    if (data) {
      this.Id = data.Id;
      this.Name = data.Name;
      this.CostLineType = data.CostLineType;
      this.Description = data.Description;
      this.Price = data.Price;
      this.IsTaxable = data.IsTaxable;
      this.IsTaxCompanyCostOnly = data.IsTaxCompanyCostOnly;
      this.IsViewable = data.IsViewable;
      this.QtyIsVisible = data.QtyIsVisible;
      this.QtyIsVisibleItemized = data.QtyIsVisibleItemized;
      this.Order = data.Order;
      this.IsTemplate = data.IsTemplate;
      this.Vendor = data.Vendor;
      this.Sku = data.Sku;
      this.DefaultQty = data.DefaultQty;
      this.DefaultCostPer = data.DefaultCostPer;
      this.MinimumMarkupType = data.MinimumMarkupType;
      this.MinimumMarkupPer = data.MinimumMarkupPer;
      this.DefaultMarkupType = data.DefaultMarkupType;
      this.DefaultMarkupPer = data.DefaultMarkupPer;
      this.SourceUrl = data.SourceUrl;
      this.CategoryId = data.CategoryId;
      this.TradeId = data.TradeId;
      this.UnitType = data.UnitType;
      this.IsOpen = data.IsOpen;
      this.OnlyShowCategoryName = data.OnlyShowCategoryName;
      this.Props = data.Props;
      this.ShowQtyTextBox = data.ShowQtyTextBox;
      this.ShowCostPerTxtBox = data.ShowCostPerTxtBox;
      this.SectionWiseOrder = data.SectionWiseOrder;
      this.isTemplateOpen = data.isTemplateOpen;
      // this.calculatedValues = data.calculatedValues;
      this.IsDirty = data.IsDirty;
      this.Units = data.Units;
      this.IsSelectedForSelection = data.IsSelectedForSelection;
      this.CreatedDate = data.CreatedDate;
    }
  }
}

export class calculatedCostlineValuesDTO {
  calculatedMarkup: number = 0;
  calculatedCompanyCost: number = 0;
  calculatedCustomerCost: number = 0;
  calculatedMinimumMarkup: number = 0;
  taxableAmt = 0;
}

export interface CostLineTypeProps {
  type: CostLineTypes;
  name: string;
  icon: string;
  dropdownEditorType: TagModalTypeEnum;
  description: string;
  categoryType: string;
  tagType: TagTypes;
}

export interface CostlineTypeWiseTotalDTO {
  TotalLaborCost: number;
  TotalMaterialCost: number;
  TotalOtherCost: number;
  TotalLaborPercent: number;
  TotalMaterialPercent: number;
  TotalOtherCostPercent: number;
  TotalCompanyCost: number;
  TotalCustomerCost: number;
  TotalLaborCostWithMarkup: number;
  TotalMaterialCostWithMarkup: number;
  TotalOtherCostWithMarkup: number;
  TotalCompanyPercent: number;
  TotalProfit: number;
  TotalMarkup: number;
  TotalMargin: number;
}

export interface EstimateStatusPropsDTO {
  key: EstimateStatusTypes;
  icon: string;
  text: string;
  color: string;
}

export class EstimateStatusesCalculationDTO {
  key: EstimateStatusTypes;
  TotalFiltered: number;
  TotalAmount: number;
}


export interface EstimateValuesDTO {
  TotalLaborCost: number;
  TotalMaterialCost: number;
  TotalOtherCost: number;
  TotalLaborPercent: number;
  TotalMaterialPercent: number;
  TotalOtherCostPercent: number;
  TotalCompanyCost: number;
  TotalWithTax: number;
  TotalWithTaxBeforeDiscount: number;
  TotalLaborCostWithMarkup: number;
  TotalMaterialCostWithMarkup: number;
  TotalOtherCostWithMarkup: number;
  TotalCompanyPercent: number;
  TotalProfit: number;
  TotalMarkup: number;
  TotalMargin: number;
  TotalTax: number;
  TaxableTotal: number;
  TotalWithoutTax: number;
  TaxPercentage: number;
  TotalInvoicedWithoutTax: number;
  TotalInvoiced: number;
  ToBeInvoiced: number;
  InvoiceRemainingBalance: number;
  PaymentToDate: number;
  TotalDiscount: number;
  TotalDiscountPercent: number;

  EstimatedTotal: number;
  RejectedTotal: any;
  AllRejected: boolean;
  InvoiceAdditionalLinesTotal: any;
  UnpublishedInvoiceAdditionalLinesTotal: any;
  ApprovedInvoiceAdditionalLinesTotal: any;
  Projected: EstimateValuesDTO;
  ProjectedProjectTotal: any;
  PublishedInvoiceAdditionalLinesTotal: any;
  UnpublishedProjectTotal: number;
  PublishedProjectTotal: number;
  ApprovedProjectTotal: number;
  Published: EstimateValuesDTO; // it has totals for published estimate and change orders.
  Unpublished: EstimateValuesDTO; // it has totals for unpublished estimate and change orders.
  Combined: EstimateValuesDTO; // it has totals for all estimate and change orders.
}


export interface DropDownDTO {
  name: string;
  code: number;
  icon: string;
  type?: string;
  class?: string;
  toggle?: boolean;
  hasToggle?: boolean;
  disabled?: boolean;
  hidden?: boolean;
}

export enum EstimateSectionDisplayRenderType {
  subsection = 1,
  costline = 2
}

export interface EstimateSectionsDisplayListDTO extends EstimateDetailsSectionEntityDTO, EstimateCostLineItemsEntityDTO {
  isNextSubsection: boolean;
  isNextCostline: boolean;
  isPreviousSubsection: boolean;
  isTemplateOpen: boolean;
  originalObject: any;
  renderedType: EstimateSectionDisplayRenderType;
  index: number;
  beforesub?: boolean
}

export class EstimateCustomerData {
  CustomerFirstName: string;
  CustomerLastName: string;
  CustomerId: string;
  CustomerAddress: EstimateAddressDTO;
  CustomerBillingAddress: EstimateAddressDTO;
  CompanyEmail: string;
  CustomerPhone: string;
  ShowBillingAddressToCustomer: boolean;
  CompanyName?: string;

  constructor() {
    this.CustomerFirstName = '';
    this.CustomerLastName = '';
    this.CustomerId = '';
    this.CustomerAddress = new EstimateAddressDTO();
    this.CustomerBillingAddress = new EstimateAddressDTO();
    this.CompanyEmail = '';
    this.CustomerPhone = '';
    this.ShowBillingAddressToCustomer = true;
    this.CompanyName = '';
  }
}

export class EstimateProjectData {
  ProjectName: string;
  ProjectNumber: string;
  ProjectId: string;
  ProjectAddress: EstimateAddressDTO;
  ShowProjectInfoToCustomer: boolean;
  PhoneNo?: string;

  constructor() {
    this.ProjectAddress = new EstimateAddressDTO();
    this.ProjectName = '';
    this.ProjectNumber = '';
    this.ProjectId = '';
    this.ShowProjectInfoToCustomer = true;
    this.PhoneNo = '';
  }
}

export class EstimateAddressDTO {
  Street1: string;
  City: string;
  State: string;
  PostalCode: string;
  Latitude?: any;
  Longitude?: any;
}


export interface EstimateSectionsDisplayListDTO extends EstimateDetailsSectionEntityDTO, EstimateCostLineItemsEntityDTO {
  isTemplateOpen: boolean;
  originalObject: any;
  renderedType: EstimateSectionDisplayRenderType;
  index: number;
  beforesub?: boolean
}

export interface LinkedDiscountDTO {
  Id: string,
  DiscountId: string,
  Name: string;
  Value: number;
  IsPercentage: boolean;
  ApplyBeforeTax: boolean;
  IsDisabled: boolean;
  DiscountedValue: number;
}
